import { RESERVATION_SOURCE_LABELS_TR } from '@okn/shared';
import {
  Armchair,
  ArrowLeft,
  BadgeCheck,
  Ban,
  DoorOpen,
  ExternalLink,
  Mail,
  Pencil,
  Phone,
  Plus,
  Send,
  Ticket,
  Undo2,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { SeatEditor } from './SeatEditor.tsx';
import { adminApi } from './auth.ts';
import { fmtDateTime, fmtRelative } from './lib/format.ts';
import type { AuditEntry } from './lib/types.ts';
import { useReservationDetail } from './lib/useReservation.ts';
import { StatusBadge } from './ui/Badge.tsx';
import { Button } from './ui/Button.tsx';
import { Card, CardHeader } from './ui/Card.tsx';
import { EmptyState } from './ui/EmptyState.tsx';
import { SeatList } from './ui/SeatList.tsx';
import { Spinner } from './ui/Spinner.tsx';
import { useToast } from './ui/Toast.tsx';

const ACTION_META: Record<string, { label: string; icon: typeof BadgeCheck; tone: string }> = {
  mark_paid: { label: 'Ödeme onaylandı', icon: BadgeCheck, tone: 'text-success' },
  cancel: { label: 'İptal edildi', icon: Ban, tone: 'text-danger' },
  manual_reserve: { label: 'Manuel rezervasyon', icon: Plus, tone: 'text-text-muted' },
  resend_email: { label: 'E-posta gönderildi', icon: Send, tone: 'text-text-muted' },
  toggle_sellable: { label: 'Koltuk durumu değişti', icon: Armchair, tone: 'text-text-muted' },
  assign_kind: { label: 'Koltuk türü atandı', icon: Armchair, tone: 'text-text-muted' },
  revert_to_reserved: { label: 'Beklemeye alındı', icon: Undo2, tone: 'text-text-muted' },
  reassign_seats: { label: 'Koltuklar düzenlendi', icon: Pencil, tone: 'text-text-muted' },
  download_invitation: { label: 'Davet kartı oluşturuldu', icon: Ticket, tone: 'text-text-muted' },
};

export function ReservationDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { state, reload } = useReservationDetail(id);
  const [busy, setBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      reload();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (state.status === 'loading' || state.status === 'idle') {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-text-faint">
        <Spinner /> Yükleniyor…
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <EmptyState
        title="Rezervasyon bulunamadı"
        description={state.message}
        action={
          <Link to="/admin/rezervasyonlar">
            <Button variant="secondary">Listeye dön</Button>
          </Link>
        }
      />
    );
  }

  const { reservation: r, seats, audit } = state.data;
  const canApprove = r.status === 'reserved';

  return (
    <div className="mx-auto max-w-5xl">
      <Link
        to="/admin/rezervasyonlar"
        className="inline-flex items-center gap-1.5 text-sm text-text-muted transition-colors hover:text-text"
      >
        <ArrowLeft className="h-4 w-4" /> Rezervasyonlar
      </Link>

      {/* Hero */}
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="kicker mb-1.5">Referans</p>
          <div className="flex flex-wrap items-center gap-3">
            <span className="mono text-3xl text-accent tnum">{r.referenceCode}</span>
            <StatusBadge status={r.status} size="lg" />
          </div>
          <h1 className="mt-3 font-display text-2xl tracking-tight">{r.fullName}</h1>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-text-muted">
            <a
              href={`tel:${r.phone}`}
              className="mono inline-flex items-center gap-1.5 tnum hover:text-text"
            >
              <Phone className="h-3.5 w-3.5" /> {r.phone}
            </a>
            {r.guardianName && (
              <span className="inline-flex items-center gap-1.5">Veli: {r.guardianName}</span>
            )}
            {r.email && (
              <span className="inline-flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> {r.email}
              </span>
            )}
          </div>
        </div>

        {/* Quick operational jumps */}
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            icon={<Ticket className="h-4 w-4" />}
            onClick={() => navigate(`/admin?focus=${r.id}`)}
          >
            Onay Masası
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={<DoorOpen className="h-4 w-4" />}
            onClick={() => navigate('/admin/salon-girisi')}
          >
            Salon
          </Button>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        {canApprove && (
          <Button
            variant="gold"
            icon={<BadgeCheck className="h-4 w-4" />}
            loading={busy}
            onClick={() =>
              act(
                () =>
                  adminApi(`/admin/reservations/${r.id}/mark-paid`, { method: 'PATCH', body: {} }),
                'Rezervasyon onaylandı',
              )
            }
          >
            Onayla
          </Button>
        )}
        {r.status === 'paid' && (
          <Button
            variant="secondary"
            icon={<Undo2 className="h-4 w-4" />}
            loading={busy}
            onClick={() =>
              act(
                () => adminApi(`/admin/reservations/${r.id}/revert`, { method: 'PATCH', body: {} }),
                'Rezervasyon beklemeye alındı',
              )
            }
          >
            Beklemeye Al
          </Button>
        )}
        <Button
          variant="secondary"
          icon={<Send className="h-4 w-4" />}
          loading={busy}
          onClick={() =>
            act(
              () =>
                adminApi(`/admin/reservations/${r.id}/resend-email`, { method: 'POST', body: {} }),
              'E-posta gönderildi',
            )
          }
        >
          E-postayı gönder
        </Button>
        {r.documentUrl && (
          <a href={r.documentUrl} target="_blank" rel="noreferrer">
            <Button variant="secondary" icon={<ExternalLink className="h-4 w-4" />}>
              Belge
            </Button>
          </a>
        )}
        {r.status !== 'cancelled' && (
          <Button
            variant="danger"
            icon={<Ban className="h-4 w-4" />}
            loading={busy}
            onClick={() => {
              if (!confirm(`${r.referenceCode} iptal edilsin mi? Koltuklar tekrar satışa açılır.`))
                return;
              act(
                () =>
                  adminApi(`/admin/reservations/${r.id}/cancel`, {
                    method: 'PATCH',
                    body: { reason: 'admin cancel' },
                  }),
                'Rezervasyon iptal edildi',
              );
            }}
          >
            İptal et
          </Button>
        )}
      </div>

      {/* Meta grid */}
      <section className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-4">
        <Cell label="Kaynak" value={RESERVATION_SOURCE_LABELS_TR[r.source]} />
        <Cell label="Koltuk" value={`${r.seatCount}`} />
        <Cell label="Oluşturma" value={fmtDateTime(r.createdAt)} />
        <Cell label="Onay" value={r.paidAt ? fmtDateTime(r.paidAt) : '—'} />
        <Cell label="E-posta gönderim" value={r.emailSentAt ? fmtDateTime(r.emailSentAt) : '—'} />
        <Cell label="İptal" value={r.cancelledAt ? fmtDateTime(r.cancelledAt) : '—'} />
        <Cell label="Referans" value={r.referenceCode} mono />
        <Cell label="Durum" value={<StatusBadge status={r.status} />} />
      </section>

      {/* Connection info — where/how the web booking was made */}
      {(r.clientGeo || r.clientDevice || r.clientIp) && (
        <section className="mt-4">
          <div className="kicker mb-2 text-text-faint">Bağlantı Bilgisi</div>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-3">
            <Cell label="Konum" value={r.clientGeo ?? '—'} />
            <Cell label="Cihaz / Tarayıcı" value={r.clientDevice ?? '—'} />
            <Cell label="IP Adresi" value={r.clientIp ?? '—'} mono />
          </div>
        </section>
      )}

      {r.note && (
        <Card className="mt-4 px-4 py-3 text-sm">
          <span className="kicker mr-2">Not</span>
          {r.note}
        </Card>
      )}

      {/* Seats + audit */}
      <section className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader
            title="Koltuklar"
            count={seats.length}
            action={
              r.status !== 'cancelled' && (
                <button
                  type="button"
                  onClick={() => setEditorOpen(true)}
                  className="inline-flex items-center gap-1.5 text-xs text-text-muted transition-colors hover:text-text"
                >
                  <Pencil className="h-3.5 w-3.5" /> Düzenle
                </button>
              )
            }
          />
          <div className="px-4 py-4">
            <SeatList seats={seats} />
          </div>
        </Card>
        <Card>
          <CardHeader title="İşlem Geçmişi" count={audit.length} />
          <Timeline audit={audit} />
        </Card>
      </section>

      <SeatEditor
        open={editorOpen}
        reservation={{
          id: r.id,
          referenceCode: r.referenceCode,
          fullName: r.fullName,
          status: r.status,
          seats,
        }}
        onClose={() => setEditorOpen(false)}
        onSaved={reload}
      />
    </div>
  );
}

function Timeline({ audit }: { audit: AuditEntry[] }) {
  if (audit.length === 0) {
    return <p className="px-4 py-6 text-sm text-text-faint">Henüz işlem kaydı yok.</p>;
  }
  return (
    <ol className="relative px-4 py-4">
      <span className="absolute top-6 bottom-6 left-[27px] w-px bg-border" aria-hidden="true" />
      {audit.map((a) => {
        const meta = ACTION_META[a.action] ?? {
          label: a.action,
          icon: Ticket,
          tone: 'text-text-muted',
        };
        const Icon = meta.icon;
        return (
          <li key={a.id} className="relative flex gap-3 pb-5 last:pb-0">
            <span className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-bg-elevated">
              <Icon className={`h-3.5 w-3.5 ${meta.tone}`} />
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-sm text-text">{meta.label}</p>
                <time
                  className="shrink-0 text-[11px] text-text-faint tnum"
                  dateTime={a.createdAt}
                  title={fmtDateTime(a.createdAt)}
                >
                  {fmtRelative(a.createdAt)}
                </time>
              </div>
              <p className="mt-0.5 text-[11px] text-text-faint">
                {a.meta?.name ?? 'Sistem'}
                {a.meta?.reason ? ` · ${a.meta.reason}` : ''}
                {a.meta?.delivered === false ? ' · gönderilemedi' : ''}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Cell({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="bg-bg-elevated px-3.5 py-3">
      <div className="text-[10px] uppercase tracking-wider text-text-faint">{label}</div>
      <div className={`mt-1 break-words text-sm text-text ${mono ? 'mono tnum' : ''}`}>{value}</div>
    </div>
  );
}
